import { lazy, type ComponentType } from "react";

/** lazy() that survives deploys. Chunk filenames are content-hashed, so a tab
 *  holding a pre-deploy index.html 404s when it first navigates to a page it
 *  hasn't visited — one hard reload picks up the fresh manifest. The
 *  sessionStorage guard prevents a reload loop if the import keeps failing. */
export function lazyRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory().then(
      (mod) => {
        sessionStorage.removeItem("chunk-reloaded");
        return mod;
      },
      (err) => {
        if (!sessionStorage.getItem("chunk-reloaded")) {
          sessionStorage.setItem("chunk-reloaded", "1");
          window.location.reload();
          return new Promise<never>(() => {}); // page is reloading
        }
        sessionStorage.removeItem("chunk-reloaded");
        throw err;
      },
    ),
  );
}

/** Full-screen spinner shown while a code-split page loads. */
export function PageFallback() {
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <span
        className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600"
        role="status"
        aria-label="Loading"
      />
    </div>
  );
}
